import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FrameCard } from './FrameCard';
import { GalleryGrid } from './GalleryGrid';
import { Images } from '../utils/cloudflareImages';
import { Blob } from './Blob';
import { BackgroundVideo } from './BackgroundVideo';

interface Service {
  id: number;
  name: string;
  duration: string;
  price: string;
  highlights: string[];
  level: string;
  image: string;
}

const services: Service[] = [
  {
    id: 1,
    name: 'Precision Box Braids',
    duration: '4-6 hours',
    price: '$150',
    highlights: [
      'Consultation included',
      'Tailored to hair type',
      'Protective styling',
      'Lasts 6-8 weeks'
    ],
    level: 'Signature',
    image: Images.serviceBoxBraids,
  },
  {
    id: 2,
    name: 'Cornrow Artistry',
    duration: '2-3 hours',
    price: '$100',
    highlights: [
      'Custom designs',
      'Sleek finish',
      'Lasts 2-4 weeks',
      'Perfect for any event',
    ],
    level: 'Classic',
    image: Images.serviceCornrows,
  },
  {
    id: 3,
    name: 'Two-Strand Twists',
    duration: '3-4 hours',
    price: '$120',
    highlights: [
      'Natural look',
      'Gentle on hair',
      'Low maintenance',
      'Versatile styling',
    ],
    level: 'Essential',
    image: Images.serviceTwists,
  },
  {
    id: 4,
    name: 'Goddess Braids',
    duration: '3-5 hours',
    price: '$180',
    highlights: [
      'Statement style',
      'Bead accessories',
      'Perfect for special events',
      'Head-turning look',
    ],
    level: 'Premium',
    image: Images.serviceGoddess,
  },
  {
    id: 5,
    name: 'Feed-In Braids',
    duration: '4-5 hours',
    price: '$160',
    highlights: [
      'Natural-looking',
      'Less tension',
      'Comfortable wear',
      'Modern technique',
    ],
    level: 'Signature',
    image: Images.serviceFeedIn,
  },
  {
    id: 6,
    name: 'Hair Maintenance',
    duration: '1-2 hours',
    price: '$80',
    highlights: [
      'Braid touchups',
      'Conditioning treatment',
      'Scalp care',
      'Fresh finish',
    ],
    level: 'Care',
    image: Images.serviceHairMaintenance,
  },
];

export const Services: React.FC = () => {
  return (
    <div className="bg-[#FAF8F4] min-h-screen pt-20 relative overflow-hidden">
      <Blob
        variant={3}
        className="-z-10 right-0 top-40 w-[500px] h-[500px]"
        style={{ opacity: 0.04 }}
      />
      <Blob
        variant={1}
        className="-z-10 left-10 bottom-[800px] w-96 h-96"
        style={{ opacity: 0.05 }}
      />
      {/* Minimal Hero */}
      <motion.section 
        className="py-32 px-6 md:px-20 relative overflow-hidden min-h-[70vh] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Background Video */}
        <BackgroundVideo videoId="c433a02ea0d928d418c94d4e7b173e93" overlayOpacity={50} />

        <div className="max-w-6xl mx-auto text-center space-y-6 relative z-10">
          <motion.p 
            className="text-sm tracking-widest uppercase text-white/90"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Our Services
          </motion.p>
          <motion.h1 
            className="font-serif text-6xl md:text-7xl text-white leading-tight drop-shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Braiding Services
          </motion.h1>
          <motion.p 
            className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed drop-shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Expert protective styling for every occasion
          </motion.p>
        </div>
      </motion.section>

      {/* Services Grid */}
      <GalleryGrid
        items={services}
        renderItem={(service) => (
          <FrameCard
            key={service.id}
            image={service.image}
            title={service.name}
            subtitle={service.duration}
            price={service.price}
            bullets={service.highlights}
            tag={service.level}
            framed={true}
          />
        )}
      />

      {/* Simple CTA */}
      <motion.section 
        className="py-32 px-6 md:px-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="font-serif text-5xl md:text-6xl text-[#0B0B0B] leading-tight">
            Ready to book?
          </h2>
          <p className="text-xl text-[#6B6970] leading-relaxed">
            Let's create your perfect style
          </p>
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-[#0B0B0B] text-white rounded-2xl font-medium hover:shadow-lg transition-all"
            >
              Schedule Appointment
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default Services;
